import { useEffect, useRef, useCallback } from 'react';
import { orderService } from '../feature/orders/orderService';

interface UseOrderRealTimeUpdatesOptions {
  orderId: string | null;
  enabled?: boolean;
  intervalMs?: number;
  onUpdate?: (order: any) => void;
}

/**
 * Custom hook for real-time order updates via polling
 * Smart polling that only runs when enabled and orderId is available
 * Fetches directly from API without triggering Redux loading states to prevent full re-renders
 */
export const useOrderRealTimeUpdates = ({
  orderId,
  enabled = false,
  intervalMs = 5000, // Default: poll every 5 seconds
  onUpdate,
}: UseOrderRealTimeUpdatesOptions) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isFetchingRef = useRef(false);
  const onUpdateRef = useRef(onUpdate);
  const lastFetchTimeRef = useRef(0);
  const minIntervalBetweenFetches = 2000; // Minimum 2 seconds between actual API calls

  // Update callback ref when it changes
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  // Fetch order function with throttling - calls API directly without Redux
  const fetchOrder = useCallback(async () => {
    if (!orderId || isFetchingRef.current) {
      console.log('🔄 Polling skipped:', { orderId, isFetching: isFetchingRef.current });
      return;
    }
    
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTimeRef.current;
    
    // Throttle: don't fetch if we just fetched recently
    if (timeSinceLastFetch < minIntervalBetweenFetches) {
      console.log('🔄 Polling throttled:', { timeSinceLastFetch, minIntervalBetweenFetches });
      return;
    }
    
    console.log('🔄 Fetching order for real-time update:', orderId);
    isFetchingRef.current = true;
    lastFetchTimeRef.current = now;
    
    try {
      // Call API directly without going through Redux to avoid loading states
      const response = await orderService.getOrder(orderId);
      
      console.log('🔄 API response received:', { success: response.success, hasData: !!response.data });
      
      if (response.success && response.data && onUpdateRef.current) {
        console.log('🔄 Calling onUpdate callback with order data');
        // Call the update callback with the order data
        // This updates panels via refs without triggering Redux re-renders
        onUpdateRef.current(response.data);
      } else {
        console.log('🔄 Not calling onUpdate:', { success: response.success, hasData: !!response.data, hasCallback: !!onUpdateRef.current });
      }
    } catch (error) {
      console.error('🔄 Error fetching order for real-time update:', error);
    } finally {
      isFetchingRef.current = false;
    }
  }, [orderId]);

  // Start polling
  const startPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    intervalRef.current = setInterval(() => {
      fetchOrder();
    }, intervalMs);
  }, [fetchOrder, intervalMs]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Manual refresh function
  const refreshOrder = useCallback(async () => {
    await fetchOrder();
  }, [fetchOrder]);

  // Effect to manage polling lifecycle
  useEffect(() => {
    console.log('🔄 Polling effect:', { enabled, orderId, hasInterval: !!intervalRef.current });
    if (enabled && orderId) {
      // Start polling after a short delay to avoid immediate fetch on mount
      const timeoutId = setTimeout(() => {
        console.log('🔄 Starting polling interval');
        startPolling();
      }, 1000);
      
      return () => {
        console.log('🔄 Cleaning up polling');
        clearTimeout(timeoutId);
        stopPolling();
      };
    } else {
      console.log('🔄 Stopping polling:', { enabled, orderId });
      stopPolling();
    }
  }, [enabled, orderId, startPolling, stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    refreshOrder,
    isPolling: enabled && orderId !== null && intervalRef.current !== null,
  };
};

